import { useCallback, useRef, useEffect } from 'react';
import { useNotificationsStore, DashboardNotification, selectUnreadNotifications } from '../stores/notifications.store';
import { GetNotificationsUseCase } from '@/features/dashboard/application/use-cases/GetNotificationsUseCase';
import type { NotificationDTO } from '@/features/dashboard/application/dto/NotificationDTO';
import { SupabaseDashboardRepository } from '@/features/dashboard/infrastructure/repositories/SupabaseDashboardRepository';
import { useAuth } from '@features/auth';
import { supabase } from '@/shared/config/supabase';

// Polling interval in milliseconds (30 seconds)
const POLLING_INTERVAL = 30000;

/**
 * Hook to use notifications store with auto-polling and realtime support
 */
export function useNotifications() {
  const { user } = useAuth();
  const notifications = useNotificationsStore((state) => state.notifications);
  const isLoading = useNotificationsStore((state) => state.isLoading);
  const error = useNotificationsStore((state) => state.error);
  const setNotifications = useNotificationsStore((state) => state.setNotifications);
  const addNotification = useNotificationsStore((state) => state.addNotification);
  const addRealtimeNotification = useNotificationsStore((state) => state.addRealtimeNotification);
  const storeMarkAsRead = useNotificationsStore((state) => state.markAsRead);
  const storeMarkAllAsRead = useNotificationsStore((state) => state.markAllAsRead);
  const storeDeleteNotification = useNotificationsStore((state) => state.deleteNotification);
  const clearAll = useNotificationsStore((state) => state.clearAll);
  const setLoading = useNotificationsStore((state) => state.setLoading);
  const setError = useNotificationsStore((state) => state.setError);
  const getUnreadCount = useNotificationsStore((state) => state.getUnreadCount);
  const getRecentNotifications = useNotificationsStore((state) => state.getRecentNotifications);

  // Repository instance for marking as read
  const repositoryRef = useRef<SupabaseDashboardRepository | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isFetchingRef = useRef(false);

  const getRepository = useCallback(() => {
    if (!repositoryRef.current) {
      repositoryRef.current = new SupabaseDashboardRepository();
    }
    return repositoryRef.current;
  }, []);

  /**
   * Map scope_type to notification type with icon
   */
  const mapScopeTypeToNotificationType = (scopeType?: string): 'info' | 'warning' | 'success' | 'error' => {
    if (!scopeType) return 'info';

    // Map scope types to visual notification types
    switch (scopeType.toUpperCase()) {
      case 'GLOBAL':
        return 'info'; // Blue info icon for global announcements
      case 'COURSE':
        return 'success'; // Green check for course-specific
      case 'LEVEL':
        return 'warning'; // Yellow warning for level-wide
      case 'USER':
        return 'info'; // Blue info for personal
      case 'ADMIN':
        return 'warning'; // Yellow warning for admin notifications (needs attention)
      default:
        return 'info';
    }
  };

  /**
   * Fetch notifications from backend
   */
  const fetchNotifications = useCallback(async (useCase?: GetNotificationsUseCase, userRole?: string) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    // Use existing repository if useCase not provided
    const repository = useCase ? null : getRepository();
    const role = userRole || user?.role || 'student';

    setLoading(true);
    setError(null);

    try {
      let notificationDTOs;
      if (useCase) {
        notificationDTOs = await useCase.execute(role);
      } else {
        notificationDTOs = await repository!.getNotifications(role);
      }

      // Map DTOs to DashboardNotification format with proper types
      const mappedNotifications: DashboardNotification[] = notificationDTOs.map((dto: NotificationDTO) => ({
        id: dto.id,
        message: dto.message,
        time: dto.time,
        type: mapScopeTypeToNotificationType(dto.scopeType),
        isRead: dto.isRead ?? false,
        createdAt: new Date(),
      }));

      setNotifications(mappedNotifications);
    } catch (err: unknown) {
      console.error('Failed to fetch notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [setLoading, setError, setNotifications, getRepository, user?.role]);

  /**
   * Refetch notifications (for polling)
   */
  const refetch = useCallback(async () => {
    if (!user?.id) return;
    await fetchNotifications();
  }, [fetchNotifications, user?.id]);

  /**
   * Mark a notification as read (both locally and in database)
   */
  const markAsRead = useCallback(async (id: string) => {
    // Update local state immediately for optimistic UI
    storeMarkAsRead(id);

    // Persist to database
    try {
      const repository = getRepository();
      await repository.markNotificationAsRead(id);
    } catch (err) {
      console.error('Failed to persist notification read status:', err);
      // Note: We don't revert the local state since it will sync on next fetch
    }
  }, [storeMarkAsRead, getRepository]);

  /**
   * Mark all notifications as read (both locally and in database)
   */
  const markAllAsRead = useCallback(async () => {
    // Update local state immediately for optimistic UI
    storeMarkAllAsRead();

    // Persist to database
    try {
      const repository = getRepository();
      await repository.markAllNotificationsAsRead();
    } catch (err) {
      console.error('Failed to persist all notifications as read:', err);
      // Note: We don't revert the local state since it will sync on next fetch
    }
  }, [storeMarkAllAsRead, getRepository]);

  /**
   * Dismiss notification (mark as read and hide from UI)
   * This is what happens when user clicks the X button
   */
  const dismissNotification = useCallback(async (id: string) => {
    // Mark as read first
    await markAsRead(id);
    // Then remove from local UI
    storeDeleteNotification(id);
  }, [markAsRead, storeDeleteNotification]);

  /**
   * Delete notification completely (from local store only)
   * Note: We don't delete from DB, just mark as read and remove locally
   */
  const deleteNotification = useCallback(async (id: string) => {
    await dismissNotification(id);
  }, [dismissNotification]);

  // Auto-fetch on mount and setup polling
  useEffect(() => {
    if (!user?.id) return;

    // Initial fetch
    refetch();

    // Setup polling
    pollingRef.current = setInterval(() => {
      refetch();
    }, POLLING_INTERVAL);

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [user?.id, refetch]);

  // Setup Supabase Realtime subscription for new notifications
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'push_notifications',
        },
        async (payload) => {
          // Check if this notification is for the current user
          const newNotif = payload.new as {
            id: number;
            title: string | null;
            message_body: string | null;
            workspace_id: number | null;
            scope_type: string | null;
            scope_reference_uuid: string | null;
            created_at: string;
          };

          // Get user's workspace
          const { data: userData } = await supabase
            .from('users')
            .select('workspace_id')
            .eq('id', user.id)
            .single();

          if (!userData?.workspace_id) return;

          // Check if notification belongs to user's workspace
          if (newNotif.workspace_id !== userData.workspace_id) return;

          // Check scope - if USER scope, must match current user
          if (newNotif.scope_type === 'USER' && newNotif.scope_reference_uuid !== user.id) {
            return;
          }

          // Add to store
          addRealtimeNotification({
            id: String(newNotif.id),
            message: newNotif.title ? `${newNotif.title}: ${newNotif.message_body ?? ''}` : (newNotif.message_body ?? ''),
            time: 'Ahora',
            type: mapScopeTypeToNotificationType(newNotif.scope_type ?? undefined),
            isRead: false,
            createdAt: new Date(newNotif.created_at),
          });

          // Play notification sound
          try {
            const audio = new Audio('/sounds/notifications.wav');
            audio.volume = 0.8;
            audio.play().catch(() => {
              // Ignore autoplay errors - browsers may block autoplay
            });
          } catch {
            // Ignore audio errors
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, addRealtimeNotification]);

  return {
    notifications,
    isLoading,
    error,
    unreadCount: getUnreadCount(),
    recentNotifications: getRecentNotifications(5), // Show last 5 notifications

    // Actions
    fetchNotifications,
    refetch,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    dismissNotification,
    clearAll,
  };
}

/**
 * Hook to get only unread notifications
 */
export function useUnreadNotifications() {
  const unreadNotifications = useNotificationsStore(selectUnreadNotifications);
  const unreadCount = useNotificationsStore((state) => state.getUnreadCount());

  return {
    unreadNotifications,
    unreadCount,
  };
}

/**
 * Hook to get recent notifications with limit
 */
export function useRecentNotifications(limit: number = 5) {
  const recentNotifications = useNotificationsStore((state) =>
    state.getRecentNotifications(limit)
  );

  return {
    recentNotifications,
  };
}
